/** 金額の表示単位。'man' は 1 万円を 1 とする表記 */
export type AmountUnit = 'yen' | 'man'

const MAN = 10000
/** 万円を小数第 1 位まで数えたときの 1 億分の刻み数（1 億 = 10,000 万 = 100,000 刻み） */
const TENTHS_PER_OKU = 100_000

const yenFormat = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 })
const plainYenFormat = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 })

/**
 * 万の位は常に小数第 1 位まで出す。桁数が値によって変わると列の中で小数点がずれて読みにくい。
 * 億と併記したときに 1億5400.5万円 と続けたいので、区切りのカンマは入れない。
 */
const manFormat = new Intl.NumberFormat('ja-JP', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
})
const okuFormat = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 })

/**
 * 万円表記。1 万円未満は万円にすると桁が潰れて読めない（3,000 円 → 0.3万円）ので、
 * そのまま円で出す。1 億以上は万の位が 5 桁になって読めないので億で括る。
 */
function formatMan(amount: number, style: 'currency' | 'plain'): string {
  if (Math.abs(amount) < MAN) {
    return style === 'currency' ? yenFormat.format(amount) : plainYenFormat.format(amount)
  }
  const sign = amount < 0 ? '-' : ''
  // 億に分ける前に万円の小数第 1 位で丸める。分けてから丸めると 9999.95 万円が
  // 1億0.0万円ではなく 0億10000.0万円 になってしまう
  const tenths = Math.round(Math.abs(amount) / 1000)
  const oku = Math.floor(tenths / TENTHS_PER_OKU)
  const man = (tenths % TENTHS_PER_OKU) / 10
  const suffix = style === 'currency' ? '万円' : '万'
  const manPart = `${manFormat.format(man)}${suffix}`
  return oku > 0 ? `${sign}${okuFormat.format(oku)}億${manPart}` : `${sign}${manPart}`
}

/**
 * 表示する桁で丸めると 0 になる値を、符号の付かない 0 に揃える。
 *
 * カードの利用額のように符号を反転して作った値は、残高 0 のとき -0 になる。
 * -0 や -0.4 をそのまま Intl に渡すと「-¥0」と出て、増減が無いのにマイナスが
 * 付いて見えてしまう。プラス側も同じ理由で 0 として扱う。
 */
export function withoutZeroSign(amount: number, fractionDigits = 0): number {
  const factor = 10 ** fractionDigits
  return Math.round(amount * factor) === 0 ? 0 : amount
}

/**
 * 増減の符号（0 に丸まる値には付けない）。符号を自前で前置して絶対値を並べる箇所で使う。
 */
export function signPrefix(amount: number, fractionDigits = 0): '' | '+' | '-' {
  const normalized = withoutZeroSign(amount, fractionDigits)
  if (normalized === 0) return ''
  return normalized > 0 ? '+' : '-'
}

/**
 * プラスのときだけ付ける符号。マイナスはフォーマッタ側が付けるので、
 * 「+1,000 / -1,000 / 0」と出したい箇所で使う。
 */
export function plusSign(amount: number, fractionDigits = 0): '' | '+' {
  return signPrefix(amount, fractionDigits) === '+' ? '+' : ''
}

/** 円記号付きの金額 */
export function formatJpy(amount: number, unit: AmountUnit): string {
  const value = withoutZeroSign(amount)
  return unit === 'man' ? formatMan(value, 'currency') : yenFormat.format(value)
}

/** マイナスを ¥ の外に出した表記（合計・残高用） */
export function formatSignedJpy(amount: number, unit: AmountUnit): string {
  const value = withoutZeroSign(amount)
  return value < 0 ? `-${formatJpy(-value, unit)}` : formatJpy(value, unit)
}

/**
 * 通貨記号なしの素の数値。明細セルのように同じ単位の値が並ぶ場所で使うので、
 * 万円表記のときは「万円」ではなく「万」だけを添える。
 */
export function formatPlain(amount: number, unit: AmountUnit): string {
  const value = withoutZeroSign(amount)
  return unit === 'man' ? formatMan(value, 'plain') : plainYenFormat.format(value)
}

/** 任意通貨の金額。円以外は表示単位の設定を無視する */
export function formatInCurrency(amount: number, currency: string, unit: AmountUnit): string {
  if (currency === 'JPY') return formatJpy(amount, unit)
  const formatter = new Intl.NumberFormat('ja-JP', { style: 'currency', currency })
  // 通貨ごとに小数の桁数が違う（USD なら 2 桁）。その桁で 0 になる値の符号を落とす
  return formatter.format(withoutZeroSign(amount, formatter.resolvedOptions().maximumFractionDigits ?? 2))
}
